import { useStore } from '@nanostores/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useCallback, useEffect, type ReactNode } from 'react';
import { DropdownButton } from './DropdownButton.client';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { StripeLogo } from '~/components/ui/logos/StripeLogo';
import { projectStore } from '~/lib/stores/project';
import { stripeStore } from '~/lib/stores/stripe';
import { dropdownAnimationVariants } from '~/utils/animationVariants';
import { classNames } from '~/utils/classNames';
import { invariant } from '~/utils/invariant';

function onClickGoToStripeDashboard() {
  window.open(`https://dashboard.stripe.com/`, '_blank');
}

export const StripeButton = memo(() => {
  const isConfigVisible = useStore(stripeStore.isConfigVisible);

  const isStripeConfigured = useStore(stripeStore.isConfigured);
  const isRefreshingProducts = useStore(stripeStore.isRefreshingProducts);

  const onToggleDropdown = useCallback(() => {
    stripeStore.isConfigVisible.set(!isConfigVisible);
  }, [isConfigVisible]);

  useEffect(() => {
    const products = stripeStore.products.get();

    if (isConfigVisible && products.length === 0) {
      const projectId = projectStore.projectId.get();

      invariant(projectId);

      stripeStore.refreshProducts(projectId);
    }
  }, [isConfigVisible]);

  if (!isStripeConfigured) {
    return null;
  }

  return (
    <>
      <DropdownMenu.Root open={isConfigVisible} modal={false} onOpenChange={onToggleDropdown}>
        <DropdownMenu.Trigger asChild>
          <LegacyIconButton className="relative" size="sm">
            <StripeLogo size="sm" />
          </LegacyIconButton>
        </DropdownMenu.Trigger>
        <AnimatePresence>
          {isConfigVisible && (
            <DropdownMenu.Portal forceMount>
              <DropdownMenu.Content
                className="z-visibility-dropdown"
                asChild
                forceMount
                avoidCollisions={false}
                sideOffset={3}
                onFocusOutside={() => stripeStore.isConfigVisible.set(false)}
                align="end"
              >
                <motion.div initial="close" animate="open" exit="close" variants={dropdownAnimationVariants}>
                  <div className="text-xs bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2 p-2 border border-bolt-elements-borderColor rounded-md select-none flex flex-col gap-2">
                    <div className="flex justify-between items-center">
                      <h3 className="text-sm font-semibold ml-2">Stripe</h3>
                      <LegacyButton
                        variant="secondary"
                        size="sm"
                        className="px-2"
                        disabled={isRefreshingProducts}
                        onClick={refreshProducts}
                      >
                        {isRefreshingProducts ? (
                          <span className="i-svg-spinners:90-ring-with-bg size-4 mr-2" />
                        ) : (
                          <i className="i-ph:arrows-clockwise size-4 mr-2" />
                        )}
                        Sync now
                      </LegacyButton>
                    </div>
                    <EditApiKey onClick={onToggleDropdown} />
                    <EditProducts onClick={onToggleDropdown} />
                    <hr className="border-bolt-elements-borderColor" />
                    <DropdownButton onClick={onClickGoToStripeDashboard}>
                      <StripeLogo size="xs" />
                      Stripe dashboard <span className="i-ph:arrow-up-right" />
                    </DropdownButton>
                  </div>

                  <DropdownMenu.Arrow className="fill-bolt-elements-borderColor" />
                </motion.div>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          )}
        </AnimatePresence>
      </DropdownMenu.Root>
    </>
  );
});

function refreshProducts() {
  const projectId = projectStore.getProjectId();

  invariant(projectId);

  stripeStore.refreshProducts(projectId);
}

interface EditApiKeyProps {
  onClick: VoidFunction;
}

const EditApiKey = memo(({ onClick }: EditApiKeyProps) => {
  const stripePartialKey = useStore(stripeStore.partialKey);
  const mode = useStore(stripeStore.mode);

  return (
    <ItemButton
      icon="i-ph:key"
      title={
        <>
          API key
          {mode && (
            <div
              className={classNames(
                'uppercase rounded font-medium px-1 py-[3.226px] line-height-[12.903px] text-[9.677px] ml-1 shrink-0 flex items-center',
                {
                  'bg-bolt-ds-warning-container text-bolt-ds-on-warning-container': mode === 'test',
                  'bg-bolt-ds-success-container text-bolt-ds-on-success-container': mode === 'live',
                },
              )}
            >
              {mode}
            </div>
          )}
        </>
      }
      secondaryTitle={stripePartialKey}
      onClick={() => {
        onClick();
        stripeStore.openModalToEditApiKey();
      }}
    />
  );
});

interface EditProductsProps {
  onClick: VoidFunction;
}

const EditProducts = memo(({ onClick }: EditProductsProps) => {
  const products = useStore(stripeStore.products);
  const selectedProducts = useStore(stripeStore.selectedProducts);

  const productAvailableCount = products.length;
  const productSelectedCount = selectedProducts.length;

  const hasProducts = productAvailableCount > 0;

  return (
    <ItemButton
      icon="i-ph:shopping-bag"
      title="Products"
      secondaryTitle={
        hasProducts ? `${productSelectedCount} of ${productAvailableCount} activated` : 'No products found'
      }
      onClick={() => {
        onClick();
        stripeStore.openModalToEditProducts();
      }}
    />
  );
});

interface ItemButtonProps {
  icon: string;
  title: ReactNode;
  secondaryTitle: ReactNode;
  onClick: VoidFunction;
}

function ItemButton({ icon, title, secondaryTitle, onClick }: ItemButtonProps) {
  return (
    <button
      className="flex flex-col gap-0.5 rounded-md bg-transparent hover:bg-bolt-ds-surfaceHighlight px-2 py-1.5 group"
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <i className={`shrink-0 ${icon} size-4 mr-2`} />
        <div className="w-40 flex flex-col gap-1">
          <div className="flex items-center">{title}</div>
          <div className="text-left text-bolt-elements-text-secondary">{secondaryTitle}</div>
        </div>
        <span className="text-bolt-ds-brand font-medium text-xs opacity-0 group-hover:opacity-100">Edit</span>
      </div>
    </button>
  );
}
